import { z } from 'zod';
import { ChannelId } from './common.js';
import { CHANNEL_ROLES, SCOPES } from '../scopes.js';

/**
 * Starting a login.
 *
 * `return_to` is a PATH, not a URL. Same-origin only, which is all the
 * first-party console needs and is the one shape that cannot be turned into
 * an open redirect. Logging in on a streamer's own domain needs the
 * handoff-code flow in ADR 0010, and that is deliberately not half-built here.
 */
export const LoginStart = z.object({
  return_to: z.string()
    .max(512)
    // Refused rather than normalised. `//evil.com` is a protocol-relative URL
    // the browser reads as another origin, and a validator that tries to clean
    // hostile input tends to lose where one that refuses to guess cannot (L48).
    //
    // Backslashes are refused ANYWHERE, not just in second place. Chrome
    // normalises `\` to `/` inside a URL, so `/\evil.com` becomes
    // `//evil.com` and lands on another origin. Whitespace goes for the same
    // reason: a header value must not be able to carry a newline.
    .regex(
      /^\/(?![/\\])[^\s\\]*$/,
      'return_to must be a path on this site, beginning with a single /'
    )
    .default('/'),
  /** Present when the login was started to administer a specific channel. */
  channel_id: ChannelId.optional(),
});

export const LoginStarted = z.object({
  /** Send the browser here. */
  authorize_url: z.string().url(),
});

/**
 * What a channel means to the signed-in person.
 *
 * `scopes` is included so the console can hide what a request would refuse.
 * That is a courtesy to the user, never a control: the server checks the role
 * again on every request, and a console that only hid the button would be a
 * console anybody could bypass with fetch.
 */
export const SessionChannel = z.object({
  id: ChannelId,
  slug: z.string(),
  display_name: z.string(),
  role: z.enum(CHANNEL_ROLES),
  scopes: z.array(z.enum(SCOPES)),
});

/**
 * Work that is built but not yet offered to everybody.
 *
 * `collectibles` is the whole rewards side of the studio — files, items,
 * rarities, rewards, bundles, triggers, collections and trades. It works end
 * to end and is not finished as a product, so it is shown only to the people
 * it has been turned on for.
 *
 * A name here is the complete list of what a flag document may switch. An
 * unknown name in the store is ignored rather than published, so a typo in a
 * console cannot put a string into every session.
 */
export const FEATURES = ['collectibles'] as const;
export type Feature = (typeof FEATURES)[number];

export const Session = z.object({
  user: z.object({
    uid: z.string(),
    handle: z.string().nullable(),
    display_name: z.string().nullable(),
    avatar_url: z.string().nullable(),
  }),
  /** Every channel this person may administer or browse as a member. */
  channels: z.array(SessionChannel),
  /**
   * Platform authority (ADR 0044), so an interface knows whether to DRAW the
   * moderation controls.
   *
   * Reported, never trusted: the server re-reads it on every request that
   * acts, so a client that lied would see buttons that answer 403. It is a
   * rendering hint and nothing else.
   */
  admin: z.boolean(),
  /**
   * The unreleased features this person may see (see FEATURES).
   *
   * Like `admin`, a rendering hint: it decides what the console DRAWS, and
   * the API does not consult it. Hiding unfinished work is not a permission.
   */
  features: z.array(z.enum(FEATURES)),
  expires_at: z.string().datetime(),
});

export type Session = z.infer<typeof Session>;
export type SessionChannel = z.infer<typeof SessionChannel>;
export type LoginStart = z.infer<typeof LoginStart>;

/**
 * A short-lived Twitch access token, for the browser's own chat connection.
 *
 * The browser speaks IRC and Helix directly (ADR 0039), so it needs a real
 * Twitch token — there is no way to hand it a tomo-shaped substitute. What it
 * does not need is a durable one: this expires in minutes, the refresh token
 * behind it never leaves the server, and a leaked access token dies on its own.
 *
 * `scopes` is what the VIEWER consented to, which can lag the current
 * LOGIN_SCOPES — somebody who signed in before chat scopes existed holds a
 * credential that cannot send. The client reads this to decide whether to
 * offer a composer or ask them to reconnect, rather than discovering it from a
 * message that silently failed.
 */
export const ChatCredential = z.object({
  access_token: z.string(),
  /** Twitch login. IRC needs it for `NICK`. */
  login: z.string(),
  /**
   * The viewer's numeric Twitch id.
   *
   * Helix identifies people by id, not login, and `sender_id` is required on
   * Send Chat Message. IRC never needs it. Returned here so the browser does
   * not have to spend a Helix call discovering who it already is.
   */
  user_id: z.string(),
  /**
   * The Twitch application's client id.
   *
   * Public by definition — it is in every authorize URL — and returned here so
   * the browser does not need a build-time copy of it. Helix requires it as a
   * header beside the token, and pairing the two means they cannot drift: a
   * token minted by one application and sent with another's id is rejected in
   * a way that reads like an expired token.
   */
  client_id: z.string(),
  expires_at: z.string().datetime(),
  scopes: z.array(z.string()),
});
